import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { listCustomers } from './api';
const PAGE_LIMIT = 20;
export const CustomerList = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [nameFilter, setNameFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    // Stack of nextTokens for pagination — index 0 = first page (undefined token)
    const [tokenStack, setTokenStack] = useState([undefined]);
    const [pageIndex, setPageIndex] = useState(0);
    const currentToken = tokenStack[pageIndex];
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await listCustomers({
                name: nameFilter || undefined,
                type: typeFilter || undefined,
                status: statusFilter || undefined,
                limit: PAGE_LIMIT,
                nextToken: currentToken,
            });
            setResult(data);
        }
        catch (e) {
            setError(e.message ?? 'Failed to load customers');
        }
        finally {
            setLoading(false);
        }
    }, [nameFilter, typeFilter, statusFilter, currentToken]);
    useEffect(() => {
        load();
    }, [load]);
    const handleSearch = () => {
        // Reset pagination on new search
        setTokenStack([undefined]);
        setPageIndex(0);
    };
    const handleNext = () => {
        if (!result?.nextToken)
            return;
        const next = pageIndex + 1;
        const newStack = tokenStack.slice(0, next);
        newStack.push(result.nextToken);
        setTokenStack(newStack);
        setPageIndex(next);
    };
    const handlePrev = () => {
        if (pageIndex === 0)
            return;
        setPageIndex(pageIndex - 1);
    };
    return (_jsx(Layout, { children: _jsxs("div", { children: [_jsxs("div", { style: styles.pageHeader, children: [_jsxs("div", { children: [_jsx("h2", { style: styles.heading, children: "Customers" }), _jsx("p", { style: styles.subheading, children: "Customer database and management" })] }), _jsx("button", { style: styles.primaryButton, onClick: () => navigate('/customers/new'), children: "+ New Customer" })] }), _jsxs("div", { style: {
                        ...styles.filtersCard,
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center',
                    }, children: [_jsx("input", { style: { ...styles.searchInput, minWidth: isMobile ? '0' : '180px' }, type: "text", placeholder: "Search by name...", value: nameFilter, onChange: (e) => setNameFilter(e.target.value), onKeyDown: (e) => e.key === 'Enter' && handleSearch() }), _jsxs("select", { style: styles.select, value: typeFilter, onChange: (e) => {
                                setTypeFilter(e.target.value);
                                setTokenStack([undefined]);
                                setPageIndex(0);
                            }, children: [_jsx("option", { value: "", children: "All Types" }), _jsx("option", { value: "commercial", children: "Commercial" }), _jsx("option", { value: "residential", children: "Residential" })] }), _jsxs("select", { style: styles.select, value: statusFilter, onChange: (e) => {
                                setStatusFilter(e.target.value);
                                setTokenStack([undefined]);
                                setPageIndex(0);
                            }, children: [_jsx("option", { value: "", children: "All Statuses" }), _jsx("option", { value: "active", children: "Active" }), _jsx("option", { value: "inactive", children: "Inactive" })] }), _jsx("button", { style: {
                                ...styles.searchButton,
                                minHeight: isMobile ? '44px' : undefined,
                            }, onClick: handleSearch, children: "Search" })] }), error && _jsx("div", { style: styles.errorBanner, children: error }), loading && !result && (_jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) })), result && result.items.length === 0 && !loading && (_jsx("p", { style: styles.emptyText, children: "No customers found." })), result && result.items.length > 0 && (_jsx("div", { style: { opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s ease' }, children: isMobile ? (
                    /* Mobile card layout */
                    _jsx("div", { children: result.items.map((c) => (_jsx(CustomerMobileCard, { customer: c, onClick: () => navigate(`/customers/${c.id}`) }, c.id))) })) : (
                    /* Desktop table layout */
                    _jsxs("div", { style: styles.listCard, children: [_jsxs("div", { style: styles.tableHeader, children: [_jsx("span", { style: { ...styles.col, flex: 3 }, children: "Name" }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: "Type" }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: "Status" }), _jsx("span", { style: { ...styles.col, flex: 2 }, children: "Primary Contact" }), _jsx("span", { style: { ...styles.col, flex: 2 }, children: "Location" })] }), result.items.map((c, idx) => (_jsx(CustomerRow, { customer: c, isLast: idx === result.items.length - 1, onClick: () => navigate(`/customers/${c.id}`) }, c.id)))] })) })), result && (result.items.length > 0 || pageIndex > 0) && (_jsxs("div", { style: styles.pagination, children: [_jsx("button", { style: styles.pageButton, onClick: handlePrev, disabled: pageIndex === 0, children: "\u2190 Previous" }), _jsxs("span", { style: styles.pageInfo, children: ["Page ", pageIndex + 1] }), _jsx("button", { style: styles.pageButton, onClick: handleNext, disabled: !result?.nextToken, children: "Next \u2192" })] }))] }) }));
};
const CustomerMobileCard = ({ customer, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const primaryAddress = customer.addresses[0];
    const location = primaryAddress ? `${primaryAddress.city}, ${primaryAddress.state}` : null;
    return (_jsxs("div", { style: {
            backgroundColor: hovered ? 'rgba(0,122,255,0.04)' : '#fff',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
        }, onClick: onClick, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }, children: [_jsx("span", { style: { fontSize: '16px', fontWeight: '700', color: '#1d1d1f' }, children: customer.name }), _jsx("span", { style: customer.status === 'active' ? styles.badgeGreen : styles.badgeGray, children: customer.status === 'active' ? 'Active' : 'Inactive' })] }), _jsx("div", { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }, children: _jsx("span", { style: customer.type === 'commercial' ? styles.badgeBlue : styles.badgeGreen, children: customer.type === 'commercial' ? 'Commercial' : 'Residential' }) }), _jsxs("p", { style: { margin: '4px 0 0 0', fontSize: '13px', color: '#86868b' }, children: [customer.primaryContact.firstName, " ", customer.primaryContact.lastName] }), customer.email && (_jsx("p", { style: { margin: '2px 0 0 0', fontSize: '13px', color: '#86868b' }, children: customer.email })), customer.phones[0] && (_jsx("p", { style: { margin: '2px 0 0 0', fontSize: '13px', color: '#86868b' }, children: customer.phones[0].number })), location && (_jsx("p", { style: { margin: '2px 0 0 0', fontSize: '13px', color: '#86868b' }, children: location }))] }));
};
const CustomerRow = ({ customer, isLast, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const primaryAddress = customer.addresses[0];
    const location = primaryAddress ? `${primaryAddress.city}, ${primaryAddress.state}` : '—';
    return (_jsxs("div", { style: {
            ...styles.row,
            borderBottom: isLast ? 'none' : '1px solid rgba(0,0,0,0.06)',
            backgroundColor: hovered ? 'rgba(0,122,255,0.04)' : 'transparent',
            cursor: 'pointer',
        }, onClick: onClick, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsx("span", { style: { ...styles.col, flex: 3, fontWeight: '600', color: '#1d1d1f' }, children: customer.name }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: _jsx("span", { style: customer.type === 'commercial' ? styles.badgeBlue : styles.badgeGreen, children: customer.type === 'commercial' ? 'Commercial' : 'Residential' }) }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: _jsx("span", { style: customer.status === 'active' ? styles.badgeGreen : styles.badgeGray, children: customer.status === 'active' ? 'Active' : 'Inactive' }) }), _jsxs("span", { style: { ...styles.col, flex: 2, color: '#3c3c43' }, children: [customer.primaryContact.firstName, " ", customer.primaryContact.lastName] }), _jsx("span", { style: { ...styles.col, flex: 2, color: '#86868b' }, children: location })] }));
};
const styles = {
    pageHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '32px',
    },
    heading: {
        fontSize: '42px',
        fontWeight: '700',
        margin: '0 0 8px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    subheading: {
        fontSize: '17px',
        fontWeight: '400',
        margin: 0,
        color: '#86868b',
    },
    primaryButton: {
        padding: '12px 24px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        letterSpacing: '-0.2px',
        whiteSpace: 'nowrap',
    },
    filtersCard: {
        display: 'flex',
        gap: '12px',
        padding: '20px 24px',
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        marginBottom: '16px',
        flexWrap: 'wrap',
    },
    searchInput: {
        flex: 1,
        minWidth: '180px',
        padding: '10px 14px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '12px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        color: '#1d1d1f',
        outline: 'none',
    },
    select: {
        padding: '10px 14px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '12px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        color: '#1d1d1f',
        cursor: 'pointer',
        outline: 'none',
    },
    searchButton: {
        padding: '10px 20px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    errorBanner: {
        padding: '14px 20px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '12px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '16px',
    },
    listCard: {
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        transition: 'opacity 0.2s ease',
    },
    tableHeader: {
        display: 'flex',
        padding: '12px 24px',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        backgroundColor: 'rgba(0,0,0,0.02)',
    },
    col: {
        fontSize: '13px',
        color: '#86868b',
        fontWeight: '500',
        letterSpacing: '-0.1px',
        paddingRight: '12px',
    },
    row: {
        display: 'flex',
        padding: '16px 24px',
        alignItems: 'center',
        transition: 'background-color 0.15s ease',
    },
    badgeBlue: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(0,122,255,0.12)',
        color: '#0071e3',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
    badgeGreen: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(52,199,89,0.12)',
        color: '#248a3d',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
    badgeGray: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(142,142,147,0.15)',
        color: '#86868b',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
    loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        padding: '60px 0',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    emptyText: {
        textAlign: 'center',
        padding: '60px 24px',
        color: '#86868b',
        fontSize: '16px',
        margin: 0,
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '24px',
    },
    pageButton: {
        padding: '10px 20px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#1d1d1f',
        cursor: 'pointer',
    },
    pageInfo: {
        fontSize: '14px',
        color: '#86868b',
        fontWeight: '500',
    },
};
//# sourceMappingURL=CustomerList.js.map